import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { EnrollmentAccessService, PromptService } from '@k12/app-services';
import { catchError, of, switchMap, take, timer } from 'rxjs';
import { ENROLLMENT_READINESS } from './enrollment-readiness';

/**
 * Landing page for a submitted registration application. The domain graph is created by an async
 * saga, so the dashboard can load before provisioning finishes and render as though the user has no
 * record at all. This page polls until the record is readable, then forwards to the dashboard.
 *
 * Portal-agnostic: what to poll for and what to call the record come from ENROLLMENT_READINESS,
 * which each portal provides on its own enrollment-complete route.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-enrollment-complete',
  standalone: true,
  templateUrl: './enrollment-complete.component.html',
  styleUrl: './enrollment-complete.component.scss'
})
export class EnrollmentCompleteComponent implements OnInit {
  /** Shown once polling gives up, so the user is never left on a spinner forever. */
  public hasTimedOut: WritableSignal<boolean> = signal(false);

  /** Guards against a second dispatch while the first is still in flight. */
  public isRetrying: WritableSignal<boolean> = signal(false);

  /**
   * The enrollment application id, shown on the timeout branch. It is the SubjectId support searches
   * the workflows dashboard on, and is the only handle the user has on a submission that never
   * provisioned.
   */
  public referenceId: WritableSignal<string> = signal('');

  private static readonly PollIntervalMs = 2000;
  private static readonly MaxAttempts = 30;

  private readonly access: EnrollmentAccessService = inject(EnrollmentAccessService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly promptService: PromptService = inject(PromptService);
  private readonly readiness = inject(ENROLLMENT_READINESS);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);

  public get recordNoun(): string {
    return this.readiness.recordNoun;
  }

  public ngOnInit(): void {
    const fromUrl = this.route.snapshot.queryParamMap.get('sessionId') ?? '';
    this.referenceId.set(fromUrl);

    // A user returning later arrives with a bare URL. The probe is keyed on their token, so it still
    // resolves their submitted application and gives the retry something to send. A failure here costs
    // only the retry affordance -- polling and the dashboard link are unaffected.
    if (!fromUrl) {
      this.access
        .getStatus(this.readiness.programType)
        .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
        .subscribe((status) => this.referenceId.set(status?.sessionId ?? ''));
    }

    this.startPolling();
  }

  /**
   * Re-dispatches the registration saga for this session. Safe to call repeatedly: the submit endpoint
   * exempts registration program types from its already-submitted short circuit precisely because their
   * provisioning is idempotent, and it preserves the original DateSubmitted so the retry does not fall
   * outside the submission window.
   */
  public retryProvisioning(): void {
    const sessionId = this.referenceId();
    if (!sessionId || this.isRetrying()) {
      return;
    }

    this.isRetrying.set(true);
    this.promptService.submitEnrollment(sessionId).subscribe({
      next: () => {
        this.isRetrying.set(false);
        this.hasTimedOut.set(false);
        this.startPolling();
      },
      error: () => this.isRetrying.set(false)
    });
  }

  public goToDashboard(): void {
    void this.router.navigate(['/mypage']);
  }

  private startPolling(): void {
    const poll = timer(0, EnrollmentCompleteComponent.PollIntervalMs).pipe(
      take(EnrollmentCompleteComponent.MaxAttempts),
      // A 404/500 while the saga is still running is expected, so errors are swallowed and retried.
      switchMap(() => this.readiness.isReady().pipe(catchError(() => of(false)))),
      takeUntilDestroyed(this.destroyRef)
    );

    const subscription = poll.subscribe({
      next: (ready) => {
        if (ready) {
          subscription.unsubscribe();
          this.notifyProvisioned();
          this.goToDashboard();
        }
      },
      complete: () => this.hasTimedOut.set(true)
    });
  }

  /**
   * This page is the only place that knows the instant the record became readable, which is what
   * makes it the right place to tell the shell to re-read. Swallowing the failure is deliberate:
   * the user is mid-navigation to a dashboard that now has a record on it, and a stale nav is a far
   * better outcome than stranding them here.
   */
  private notifyProvisioned(): void {
    try {
      this.readiness.onProvisioned?.();
    } catch (error) {
      console.error('[enrollment-complete] Refreshing the portal shell after provisioning failed.', error);
    }
  }
}
